import {
  Component,
  Input,
  forwardRef,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface MultiSelectOption {
  uuid: string;
  name: string;
}

@Component({
  selector: 'app-multi-select-dropdown',
  standalone: false,
  templateUrl: './multi-select-dropdown.component.html',
  styleUrls: ['./multi-select-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectDropdownComponent),
      multi: true,
    },
  ],
})
export class MultiSelectDropdownComponent
  implements ControlValueAccessor, AfterViewInit, OnDestroy
{
  @Input() options: MultiSelectOption[] = [];
  @Input() placeholder = 'Select options';
  @Input() searchPlaceholder = 'Search...';
  /** When false, the in-dropdown search box is hidden (e.g. short, fixed option lists). */
  @Input() showSearch = true;
  @Input() noResultsLabel = 'No results found';
  @Input() selectAllLabel = '';
  @Input() hasError = false;

  /**
   * Count-summary display mode. When true, the trigger shows
   * "{n} {selectedCountLabel}" plus a count badge instead of inline chips,
   * and the inline clear button is hidden. Selected items are expected to be
   * rendered as chips by the parent (e.g. below the controls bar) per the
   * charity-filter Figma design.
   */
  @Input() showSelectionCount = false;

  /**
   * The label that follows the count in count-summary mode (e.g. "Selected"
   * renders as "8 Selected"). Provided already translated by the parent.
   */
  @Input() selectedCountLabel = '';

  /**
   * Sentinel value of a synthetic "All …" option (e.g. "All Charities"). In
   * count-summary mode, when it is the whole selection the trigger shows that
   * option's own name instead of "1 {selectedCountLabel}", and the count badge
   * is hidden. Ignored when null (the default), so other usages are unaffected.
   */
  @Input() allOptionValue: string | null = null;

  /**
   * When true, long option labels in the dropdown panel are shown in full
   * (they wrap to multiple lines) instead of being truncated with an ellipsis.
   * Use in roomy containers (e.g. the filters dialog). Defaults to false so the
   * narrow toolbar usage keeps option text contained on a single line.
   */
  @Input() optionsOverflow = false;

  /**
   * When false, the per-chip remove ("x") button is hidden on the selected
   * chips in the trigger. Used by the Status / User Type filters, where chips
   * are cleared via the dropdown options or the clear-all button instead.
   */
  @Input() showChipRemove = true;

  @ViewChild('inputEl') inputElRef!: ElementRef<HTMLElement>;

  isOpen = false;
  selectedValues: string[] = [];
  searchText = '';
  isDisabled = false;

  private containerWidth = 0;
  private resizeObserver: ResizeObserver | null = null;

  private onChange: (value: string[]) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private cdr: ChangeDetectorRef) {}

  // ─── Dropdown ─────────────────────────────────────────────────────

  toggleDropdown(): void {
    if (this.isDisabled) return;
    this.isOpen = !this.isOpen;
    if (!this.isOpen) {
      this.searchText = '';
      this.onTouched();
    }
  }

  closeDropdown(): void {
    this.isOpen = false;
    this.searchText = '';
    this.onTouched();
  }

  // ─── Filtering ───────────────────────────────────────────────────

  getFilteredOptions(): MultiSelectOption[] {
    if (!this.searchText.trim()) return this.options;
    const searchLower = this.searchText.toLowerCase();
    return this.options.filter((o) => o.name.toLowerCase().includes(searchLower));
  }

  // ─── Selection ───────────────────────────────────────────────────

  isSelected(uuid: string): boolean {
    return this.selectedValues.includes(uuid);
  }

  toggleOption(uuid: string): void {
    if (this.selectedValues.includes(uuid)) {
      this.selectedValues = this.selectedValues.filter((v) => v !== uuid);
    } else {
      this.selectedValues = [...this.selectedValues, uuid];
    }
    this.onChange(this.selectedValues);
    this.cdr.markForCheck();
  }

  clearAll(event: Event): void {
    event.stopPropagation();
    this.selectedValues = [];
    this.onChange(this.selectedValues);
    this.cdr.markForCheck();
  }

  /**
   * True when the synthetic "All …" option (`allOptionValue`) is the entire
   * current selection — the trigger then reads as that option instead of a count.
   */
  get isAllOptionOnlySelection(): boolean {
    return (
      this.allOptionValue !== null &&
      this.selectedValues.length === 1 &&
      this.selectedValues[0] === this.allOptionValue
    );
  }

  /** The synthetic "All …" option's label, shown in place of the count. */
  get allOptionLabel(): string {
    return this.options.find((o) => o.uuid === this.allOptionValue)?.name ?? '';
  }

  isAllSelected(): boolean {
    return (
      this.options.length > 0 && this.options.every((o) => this.selectedValues.includes(o.uuid))
    );
  }

  toggleSelectAll(): void {
    if (this.isAllSelected()) {
      this.selectedValues = [];
    } else {
      this.selectedValues = this.options.map((o) => o.uuid);
    }
    this.onChange(this.selectedValues);
    this.cdr.markForCheck();
  }

  // ─── Lifecycle ───────────────────────────────────────────────────

  ngAfterViewInit(): void {
    if (this.inputElRef?.nativeElement) {
      this.containerWidth = this.inputElRef.nativeElement.clientWidth;
      this.resizeObserver = new ResizeObserver((entries) => {
        this.containerWidth = entries[0]?.contentRect.width ?? 0;
        this.cdr.markForCheck();
      });
      this.resizeObserver.observe(this.inputElRef.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  // ─── Chips ───────────────────────────────────────────────────────

  getSelectedChips(): MultiSelectOption[] {
    return this.selectedValues
      .map((uuid) => this.options.find((o) => o.uuid === uuid))
      .filter((o): o is MultiSelectOption => o !== undefined);
  }

  get visibleChips(): MultiSelectOption[] {
    const chips = this.getSelectedChips();
    if (!this.containerWidth || chips.length === 0) return chips;

    // A single selected option should always be shown as its chip,
    // never collapsed into a "+1 more" badge.
    if (chips.length === 1) return chips;

    // Available width: container minus right-side controls (clear btn ~24px + arrow ~24px + padding 10px left + 6px buffer)
    const available = this.containerWidth - 64;
    if (available <= 0) return chips;

    const MORE_CHIP_WIDTH = 72; // approximate width of "+N more" badge
    const GAP = 6;
    let usedWidth = 0;

    for (let i = 0; i < chips.length; i++) {
      const chipWidth = this.estimateChipWidth(chips[i].name);
      const isLastChip = i === chips.length - 1;

      if (isLastChip) {
        // Last chip: fits without needing a "+more" badge
        if (usedWidth + chipWidth <= available) return chips;
        return chips.slice(0, i);
      }

      // For non-last chips, reserve space for the "+more" badge after this one
      if (usedWidth + chipWidth + GAP + MORE_CHIP_WIDTH <= available) {
        usedWidth += chipWidth + GAP;
      } else {
        return chips.slice(0, i);
      }
    }

    return chips;
  }

  get hiddenChipCount(): number {
    return this.getSelectedChips().length - this.visibleChips.length;
  }

  private estimateChipWidth(name: string): number {
    // 10px left pad + text (capped at 120px) + 6px gap + 14px remove icon + 6px right pad + 2px border
    const textWidth = Math.min(name.length * 7, 120);
    return textWidth + 38;
  }

  removeChip(uuid: string, event: Event): void {
    event.stopPropagation();
    this.toggleOption(uuid);
  }

  // ─── ControlValueAccessor ────────────────────────────────────────

  writeValue(value: string[]): void {
    this.selectedValues = value || [];
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.cdr.markForCheck();
  }
}
